import { Text } from '@chakra-ui/react'
import { DateTime } from 'luxon'

import { pluralize } from '@/utils/helpers'
import { toHumanReadableString } from '@/utils/time'

import SeriesQ from '@/queries/SeriesQ'
import SeriesReservationQ from '@/queries/SeriesReservationQ'
import { useUser } from '@/client/context/user'
import { useServerNow } from '@/client/hooks'
import type { Query } from '@/client/query'
import { getSeriesUrl } from '@/client/services/url'

import SeriesReservationLinkCard from '../SeriesReservationLinkCard'

export default function SeriesReservationBanner({ series }: { series: Query }): JSX.Element | null {
  const currentUser = useUser()
  const now = useServerNow()

  const seriesId = series(SeriesQ.id) ?? 0

  const countReservations =
    series(
      SeriesQ.countActiveReservations({
        reserverUserId: currentUser.id,
        userIdToExclude: SeriesReservationQ.reserver_user_id,
      })
    ) ?? 0

  const firstExpireDate = series<string>`MIN(${SeriesReservationQ.expired_at})
    ${SeriesQ.getActiveReservations({ reserverUserId: currentUser.id, userIdToExclude: SeriesReservationQ.reserver_user_id })}`

  const firstExpireDateTime = DateTime.fromISO(`${firstExpireDate}`)

  const referralLink = getSeriesUrl(seriesId, { referralUserId: currentUser.id })

  if (countReservations === 0) {
    return null
  }

  return (
    <SeriesReservationLinkCard
      referralLink={referralLink}
      header={() => (
        <Text color="fg">
          You have reserved <b>{pluralize(countReservations, 'additional spot')}</b> for this
          series. Copy this link to invite your{' '}
          {pluralize(countReservations, 'friend', { unitsOnly: true })} before their{' '}
          {countReservations === 1 ? 'spot expires' : 'spots expire'}{' '}
          <b>{toHumanReadableString(firstExpireDateTime.diff(now))}.</b>
        </Text>
      )}
    />
  )
}
